/* Solitaire — 10.30 lookbook early-access page (sections/sol-lookbook.liquid).
   Everything it needs is in the section's markup: the drop time and the Postscript number/body as
   data-* on [data-sol-lb], the sets as rendered HTML. Behaviour:
     - pinned strips: vertical scroll drives each set's photo strip sideways
     - clips play only while on screen
     - phones: the four cards trade views on their own (desktop: hover)
     - sign-up links become the final sms: URI (ruling 2026-09-30); "Text KEYWORD to N" shows only if
       the tap did not leave the page; Meta Lead on tap
     - "Enter password" presses Horizon's own password button (the form stays the theme's);
       ?unlock (or #password) in the URL presses it on arrival
     - countdown in the hero and on the docked phone button */
(() => {
  const root = document.querySelector('[data-sol-lb]');
  if (!root) return;
  const $ = (s, r = root) => r.querySelector(s);
  const $$ = (s, r = root) => [...r.querySelectorAll(s)];
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hover = matchMedia('(hover: hover)').matches;
  const pad2 = n => String(n).padStart(2, '0');

  /* ---------- hint + phone auto-flip ---------- */
  const hint = $('[data-sol-hint]');
  if (hover && hint && hint.dataset.hoverText) hint.textContent = hint.dataset.hoverText;
  if (!hover && !reduce) {
    $$('.sol-lb__card').forEach((c, i) => {
      if (c.querySelectorAll('.sol-lb__frame img').length < 2) return;
      setTimeout(() => setInterval(() => c.classList.toggle('is-alt'), 3200), 700 + i * 800);
    });
  }

  /* ---------- pinned strips ---------- */
  const chapters = $$('.sol-lb__ch').map(sec => ({ sec, track: $('.sol-lb__track', sec), wrap: $('.sol-lb__wrap', sec), bar: $('.sol-lb__prog', sec), travel: 0 }));
  function layout() {
    if (reduce) return;
    const vh = innerHeight;
    chapters.forEach(c => {
      c.track.style.transform = '';
      c.travel = Math.max(0, c.track.scrollWidth - c.wrap.clientWidth);
      c.sec.style.height = (vh + c.travel * 1.05) + 'px';
    });
    onScroll();
  }
  let ticking = false;
  function onScroll() {
    ticking = false;
    const vh = innerHeight;
    chapters.forEach(c => {
      const r = c.sec.getBoundingClientRect(), span = r.height - vh;
      const p = span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 0;
      c.track.style.transform = `translate3d(${-p * c.travel}px,0,0)`;
      c.bar.style.setProperty('--p', p);
    });
  }
  addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true });
  let rt = 0;
  addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(layout, 120); });
  addEventListener('load', layout);
  $$('.sol-lb__track img').forEach(im => { if (!im.complete) im.addEventListener('load', layout, { once: true }); });
  layout();

  /* ---------- clips play only while on screen; a set's photos start loading one screen early ---------- */
  if ('IntersectionObserver' in window) {
    const warm = new IntersectionObserver(es => es.forEach(e => {
      if (!e.isIntersecting) return;
      $$('img[loading="lazy"]', e.target).forEach(im => { im.loading = 'eager'; });
      warm.unobserve(e.target);
    }), { rootMargin: '100% 0px' });
    chapters.forEach(c => warm.observe(c.sec));
    const io = new IntersectionObserver(es => es.forEach(e => {
      const v = e.target;
      if (e.isIntersecting) { if (!v.src) v.src = v.dataset.src; const p = v.play(); if (p) p.catch(() => {}); }
      else v.pause();
    }), { threshold: .35 });
    $$('.sol-lb__panel video').forEach(v => io.observe(v));
  }

  /* ---------- sign-up ----------
     Primary lane (Jalen 2026-10-25): the Postscript EMBEDDED FORM in a dialog — the shopper types
     their number and the confirmation code without leaving the page. Fallback lane: the sms: deep
     link (ruling 2026-09-30), used automatically when the form id is unset or the SDK hasn't
     defined the element by tap time (blocked, offline, ancient browser). */
  const signup = $('[data-sol-signup]');
  const formId = root.dataset.psForm;
  const num = root.dataset.smsNumber, body = root.dataset.smsBody;
  const ios = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const smsHref = num && body ? (ios ? `sms://${num}&body=${encodeURIComponent(body)}` : `sms:${num}?&body=${encodeURIComponent(body)}`) : '';
  const fallback = $('[data-sol-fallback]');
  $$('[data-sol-sms]').forEach(a => {
    if (smsHref) a.href = smsHref;
    a.addEventListener('click', e => {
      if (signup && formId && window.customElements && customElements.get('postscript-embedded-form')) {
        e.preventDefault();
        signup.showModal();
        return;
      }
      // sms fallback: if the tap didn't leave the page, surface the text-us line
      setTimeout(() => { if (document.visibilityState === 'visible' && fallback) fallback.hidden = false; }, 1500);
    });
  });
  if (signup) {
    const closeBtn = $('[data-sol-signup-close]', signup);
    if (closeBtn) closeBtn.addEventListener('click', () => signup.close());
    signup.addEventListener('click', e => { if (e.target === signup) signup.close(); });
  }

  /* ---------- Meta pixel: Lead ONLY on the form's success screen (Jalen 2026-10-25: "only count
     when they get to the success page. After they successfully enter their phone number and type
     in the code"). The SDK emits `postscript:embedded-form` (bubbles, composed) with detail.type
     one of mount | impression | complete; `complete` carries a subscriberId and a subscriberStatus
     (new | existing). Sent through the pixel's direct tr endpoint: deterministic and dedup-guarded.
     (The 2026-10-25 "fbq is suppressed here" reading was a headless-UA artefact: real shoppers'
     fbq does send PageView from this page. See SITE_GUIDE.) */
  const PIXEL = root.dataset.pixel;
  document.addEventListener('postscript:embedded-form', e => {
    const d = e.detail || {};
    // window.__solLeadSent: page-wide guard shared with the card pop-up's listener — one signup, one Lead
    if (!PIXEL || window.__solLeadSent || d.type !== 'complete') return;
    // B-048: the SDK also sends `complete` for EXISTING subscribers (on page load for a known visitor, and
    // when one re-enters their number). Only a brand-new subscriber is a Lead (ruling 2026-11-02 (c)).
    if (d.subscriberStatus !== 'new' || !d.subscriberId) return;
    if (formId && d.formId && String(d.formId) !== String(formId)) return;
    window.__solLeadSent = true;
    // attribution markers, like fbevents sends them: _fbp cookie + fbc built from the ad click id —
    // without these, Meta books the Lead but can't credit it to the ad (observed 2026-10-25: ad-driven
    // signups counting as organic).
    let extra = '';
    const fbp = (document.cookie.match(/(?:^|;\s*)_fbp=([^;]+)/) || [])[1];
    if (fbp) extra += '&fbp=' + encodeURIComponent(fbp);
    const fbc = (document.cookie.match(/(?:^|;\s*)_fbc=([^;]+)/) || [])[1];
    const fbclid = new URLSearchParams(location.search).get('fbclid');
    if (fbc) extra += '&fbc=' + encodeURIComponent(fbc);
    else if (fbclid) extra += '&fbc=' + encodeURIComponent('fb.1.' + Date.now() + '.' + fbclid);
    new Image().src = 'https://www.facebook.com/tr?id=' + PIXEL + '&ev=Lead' +
      '&dl=' + encodeURIComponent(location.href) + '&rl=' + encodeURIComponent(document.referrer) +
      '&ts=' + Date.now() + '&cd[content_name]=' + encodeURIComponent('Solitaire early access signup') +
      extra + '&noscript=1';
  });

  /* ---------- "Enter password" = the theme's own password button ---------- */
  const openPassword = () => {
    const btn = document.querySelector('.password-enter__button');
    if (btn) btn.click();
    return !!btn;
  };
  $$('[data-sol-enter]').forEach(a => a.addEventListener('click', e => {
    if (openPassword()) e.preventDefault();  // no theme button: let the link fall through
  }));
  // The early-access text links to /password?unlock: land with the password box already open.
  // Retried briefly because the theme's dialog element may not be upgraded yet on first paint.
  if (new URLSearchParams(location.search).has('unlock') || location.hash.startsWith('#password')) {
    let tries = 0;
    const tryOpen = () => {
      const dlg = document.querySelector('.password-dialog');
      if (dlg && dlg.open) return;
      openPassword();
      if (++tries < 20) setTimeout(tryOpen, 150);
    };
    tryOpen();
  }

  /* ---------- countdown ---------- */
  const box = $('[data-sol-timer]'), dock = $('[data-sol-dock-clock]');
  const drop = new Date(root.dataset.drop).getTime();
  if (box && !isNaN(drop)) {
    const tick = () => {
      const l = drop - Date.now();
      if (l <= 0) { box.innerHTML = '<div><b>LIVE NOW</b></div>'; if (dock) dock.textContent = ''; clearInterval(iv); return; }
      const d = Math.floor(l / 864e5), h = Math.floor(l / 36e5) % 24, m = Math.floor(l / 6e4) % 60, s = Math.floor(l / 1e3) % 60;
      $('[data-d]', box).textContent = d; $('[data-h]', box).textContent = pad2(h);
      $('[data-m]', box).textContent = pad2(m); $('[data-s]', box).textContent = pad2(s);
      if (dock) dock.textContent = `${d}D ${pad2(h)}:${pad2(m)}:${pad2(s)}`;
    };
    const iv = setInterval(tick, 1000); tick();
  }
})();
